import { memo } from "react";

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion.tsx";
import { useStepValue } from "@/hooks/useStepValue";
import { cn } from "@/lib/utils";
import {
  ColorMap,
  CustomCategoryValue,
  CustomRangeValue,
  DataSchemaType,
  Dataset,
  SingleStyleValue
} from "@/types/dataset";
import { getColorScaleOptions, isIntegerDataType } from "@/utils/dataset";
import { getFillColumnOptions } from "@/utils/datasetAttributes";
import { isRankableAttribute } from "@/utils/map/rank";
import type { ConfigCallbacks } from "@/utils/mapLegend";

import ColorBasedSelect from "../ColorBasedSelect";
import ColorScaleSelect from "../ColorScaleSelect";
import OpacitySlider from "../OpacitySlider";
import CustomCategoryConfig from "./CustomCategoryConfig";
import CustomRangeConfig from "./CustomRangeConfig";
import { LabelSettings } from "./LabelSettings";
import { RankSettings } from "./RankSettings";
import SingleStyleConfig from "./SingleStyleConfig";
import StrokeWidthSlider from "./StrokeWidthSlider";

const TYPE_LABEL = {
  fill: "Fill",
  label: "Label",
  stroke: "Stroke"
};

interface CategoryConfig {
  value: string;
  onChange?: (value: string) => void;
  title?: string;
}

interface ColorBasedOnConfig {
  value?: string;
  onChange?: (value: string) => void;
  /** Data type of the colored attribute, used to filter applicable options. */
  dataType?: DataSchemaType;
}

interface ColorConfig {
  value: ColorMap;
  onChange: (value: ColorMap) => void;
  step?: number;
  onStepChange?: (step: number) => void;
}

interface RankConfig {
  onChangeSlider?: (value: number[]) => void;
  onChangeInput?: (e: number) => void;
  onCheck?: (value: boolean) => void;
}

interface StrokeConfig {
  value: number;
  onChange: (value: number) => void;
}

interface LabelConfig {
  show?: boolean;
  onCheck?: (value: boolean) => void;
}

interface DatasetStyleSelectionProps {
  type: "fill" | "label" | "stroke";
  data: Dataset;
  totalRows?: number;
  isEditMode?: boolean;
  isSpatialAnalysis?: boolean;
  isLayerVisible?: boolean;
  /** Locks every value control while keeping the panel expandable, so a
   * read-only surface (e.g. the Analysis Config view-only panel) can still show
   * which style configuration is in use. Orthogonal to `isEditMode`, which
   * *hides* the edit-mode controls instead of disabling them. */
  isReadOnly?: boolean;
  /** Disables the panel so it can't be expanded, e.g. for a dataset that
   * isn't loaded yet */
  isLocked?: boolean;
  isNewUIDatasetConfigEnabled?: boolean;
  isRankingLabelEnabled?: boolean;
  isCustomMapVisualizationLegendEnabled?: boolean;
  categoryConfig: CategoryConfig;
  colorBasedOnConfig?: ColorBasedOnConfig;
  colorConfig: ColorConfig;
  labelConfig?: LabelConfig;
  rankConfig?: RankConfig;
  strokeConfig?: StrokeConfig;
  singleStyle?: ConfigCallbacks<SingleStyleValue>;
  customCategory?: ConfigCallbacks<CustomCategoryValue>;
  customRange?: ConfigCallbacks<CustomRangeValue>;
}

/**
 * Custom-legend state for a panel: whether it applies, the attribute it styles
 * (stroke for lines, else fill), and the active method.
 */
const getCustomLegendState = (
  data: Dataset,
  type: DatasetStyleSelectionProps["type"],
  isEnabled: boolean,
  colorBasedOnValue: string
) => {
  const isStrokeLine =
    type === "stroke" && data.properties.spatialType === "line";
  const supportsCustomLegend = isEnabled && (type === "fill" || isStrokeLine);
  const styledCategory = isStrokeLine
    ? (data.categories.find(
        (category) =>
          category.attribute_name === data.style.stroke?.attribute_name
      ) ?? data.selectedCategory)
    : data.selectedCategory;

  return {
    supportsCustomLegend,
    styledCategory,
    isSingleStyle: supportsCustomLegend && colorBasedOnValue === "simple",
    isCustomCategory:
      supportsCustomLegend && colorBasedOnValue === "custom_ordinal",
    isCustomRange: supportsCustomLegend && colorBasedOnValue === "custom_breaks"
  };
};

export const DatasetStyleSelection = memo(
  (props: DatasetStyleSelectionProps) => {
    const {
      type,
      data,
      totalRows = 0,
      isEditMode,
      isSpatialAnalysis,
      isLayerVisible,
      isReadOnly = false,
      isLocked = false,
      isNewUIDatasetConfigEnabled,
      isRankingLabelEnabled,
      isCustomMapVisualizationLegendEnabled = false,
      categoryConfig,
      colorBasedOnConfig,
      colorConfig,
      labelConfig,
      rankConfig,
      strokeConfig,
      singleStyle,
      customCategory,
      customRange
    } = props;

    const schema =
      data.data.schema.length > 0 ? data.data.schema : data.categories;

    const isRankable =
      type === "label" &&
      isRankableAttribute(schema, data.style.labelConfig.attribute_name);

    const filteredCategories = data.categories.filter(
      (category) => category.is_visible
    );
    // The Fill column is the timeseries y-axis, so it must be numeric
    const columnOptionCategories =
      type === "fill"
        ? getFillColumnOptions(
            filteredCategories,
            data.data.timeseries,
            data.group_by
          )
        : filteredCategories;

    const { maxStepValue } = useStepValue({
      totalRows
    });

    // Render condition flags

    const shouldShowLabelSettings = type === "label" && labelConfig;
    const shouldShowRankSettings =
      type === "label" && isRankingLabelEnabled && rankConfig;
    const shouldShowStrokeWidthSlider = type === "stroke" && strokeConfig;

    // Disabled state for fill type on line datasets
    const isDisabled =
      isLocked ||
      (type === "fill" && data.properties.spatialType === "line") ||
      (type === "stroke" && data.properties.spatialType !== "line");

    /** Extends the existing hidden-layer lock with read-only mode. Deliberately
     * kept off `isDisabled`, which also locks the accordion trigger — expanding
     * stays available so the config is readable.
     *
     * Only applied where a hidden-layer lock already existed. The color scale,
     * opacity and stroke-width controls stay editable for a hidden layer (their
     * current behaviour) and take `isReadOnly` alone. */
    const isValueEditingDisabled = !isLayerVisible || isReadOnly;

    // "Color Based On" value is derived straight from props — `DatasetItem`
    // already resolves a default that is applicable to the current attribute
    // (falling back from an invalid stored method), and selecting a new value
    // flows back through `colorBasedOnConfig.value`. No local state/effect sync.
    const colorBasedOnValue = colorBasedOnConfig?.value ?? "";

    // The panel's baseline is the last-SAVED config; it previews edits live and
    // reverts to this on Cancel. Falls back to the live config for older layers
    // seeded before the saved slot existed.
    const singleStyleValue =
      data.style.singleStyleConfigSaved ?? data.style.singleStyleConfig;

    // Custom-legend state (applies-here, styled attribute, active method). Lines
    // carry it on the Stroke panel; every other geometry on the Fill panel.
    const {
      supportsCustomLegend,
      styledCategory,
      isSingleStyle,
      isCustomCategory,
      isCustomRange
    } = getCustomLegendState(
      data,
      type,
      isCustomMapVisualizationLegendEnabled,
      colorBasedOnValue
    );
    return (
      <AccordionItem value={type} aria-disabled={isDisabled}>
        <div
          className={cn(
            "border border-[#e5e7eb] rounded-xl px-3",
            isDisabled && "bg-whisper pointer-events-none"
          )}
        >
          <div className="flex justify-between items-center">
            <p
              className={cn(
                "text-txtHeading text-[12px] font-semibold",
                isDisabled && "text-light-gray"
              )}
            >
              {TYPE_LABEL[type]}
            </p>
            <AccordionTrigger
              className="h-[30px]"
              data-testid={`expand-dataset-trigger-${type}`}
              disabled={isDisabled}
            />
          </div>
          <AccordionContent className="flex flex-col gap-2 pt-2">
            {/* Column Option */}
            <ColorBasedSelect
              disabled={isValueEditingDisabled}
              value={categoryConfig.value}
              onValueChange={categoryConfig.onChange}
              categories={columnOptionCategories}
              title={categoryConfig.title}
            />

            {/* Color Based On (Color Scale) — Fill panel for all geometries, plus
                the Stroke panel for lines. */}
            {supportsCustomLegend && (
              <ColorBasedSelect
                disabled={isValueEditingDisabled}
                value={colorBasedOnValue}
                onValueChange={colorBasedOnConfig?.onChange}
                options={getColorScaleOptions(colorBasedOnConfig?.dataType)}
                title="Color Based On"
                placeholder="Select a color based on"
                triggerTestId="color-based-on-select-trigger"
              />
            )}

            {/* Label-specific components */}
            {shouldShowLabelSettings && (
              <LabelSettings
                disabled={isValueEditingDisabled}
                showLabel={labelConfig.show || false}
                onCheckDisplayLabel={labelConfig.onCheck}
              />
            )}

            {/* Edit mode components */}
            {isEditMode && (
              <>
                {isSingleStyle ? (
                  <SingleStyleConfig
                    // Remount when the column changes so the (default) label
                    // re-hydrates from the freshly-updated committed config.
                    key={`simple-${styledCategory?.attribute_name}`}
                    attributeLabel={categoryConfig.value}
                    isPOI={data.properties.spatialType === "poi"}
                    strokeOnly={type === "stroke"}
                    disabled={isValueEditingDisabled}
                    value={singleStyleValue}
                    onPreview={singleStyle?.onPreview}
                    onSave={singleStyle?.onSave}
                    onCancel={singleStyle?.onCancel}
                    onReset={singleStyle?.onReset}
                  />
                ) : (
                  <ColorScaleSelect
                    value={colorConfig.value}
                    onValueChange={colorConfig.onChange}
                    type={type as "fill" | "stroke"}
                    stepValue={colorConfig.step || 0}
                    onStepValueChange={colorConfig.onStepChange}
                    isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
                    maxStepValue={maxStepValue}
                    disabled={isReadOnly}
                  />
                )}
                {isCustomCategory && (
                  <CustomCategoryConfig
                    // Re-mount when the column changes so the panel re-seeds for
                    // the new attribute (the store regenerates the config in
                    // parallel) — intentional "key as reset" on identity change.
                    key={styledCategory?.attribute_name ?? ""}
                    isPOI={data.properties.spatialType === "poi"}
                    strokeOnly={type === "stroke"}
                    disabled={isValueEditingDisabled}
                    colorSteps={colorConfig.step ?? data.style.colorSteps}
                    colorFamily={data.style.colorFamily}
                    datasetId={data.data.id}
                    selectedCategory={styledCategory}
                    // Falls back to the live (auto-seeded) config so a panel
                    // opened without the dataset's rows — the read-only Analysis
                    // Config — keeps the real category labels instead of
                    // re-deriving generic "Category N" ones.
                    value={
                      data.style.customCategoryConfigSaved ??
                      data.style.customCategoryConfig
                    }
                    onPreview={customCategory?.onPreview}
                    onSave={customCategory?.onSave}
                    onCancel={customCategory?.onCancel}
                    onReset={customCategory?.onReset}
                    onRevert={customCategory?.onRevert}
                    onStepCountChange={customCategory?.onStepCountChange}
                  />
                )}
                {isCustomRange && (
                  <CustomRangeConfig
                    // Re-mount when the column changes so the panel re-seeds for
                    // the new attribute (the store regenerates the config in
                    // parallel) — intentional "key as reset" on identity change.
                    key={styledCategory?.attribute_name ?? ""}
                    isPOI={data.properties.spatialType === "poi"}
                    strokeOnly={type === "stroke"}
                    disabled={isValueEditingDisabled}
                    colorSteps={colorConfig.step ?? data.style.colorSteps}
                    colorFamily={data.style.colorFamily}
                    min={customRange?.customRangeBounds?.min}
                    max={customRange?.customRangeBounds?.max}
                    isInteger={isIntegerDataType(styledCategory?.data_type)}
                    hasEmptyValues={customRange?.customRangeHasEmptyValues}
                    value={data.style.customRangeConfigSaved}
                    // The remembered step, not the rendered break count: only a
                    // step change moves it, so a data reconcile can't look like one.
                    stepSetting={
                      data.style.colorScaleSettings?.custom_breaks?.steps
                    }
                    onPreview={customRange?.onPreview}
                    onSave={customRange?.onSave}
                    onCancel={customRange?.onCancel}
                    onReset={customRange?.onReset}
                    onRevert={customRange?.onRevert}
                  />
                )}
                <OpacitySlider
                  data={data}
                  isSpatialAnalysis={isSpatialAnalysis}
                  type={type}
                  disabled={isReadOnly}
                />
              </>
            )}

            {/* Label-specific: Rank settings */}
            {shouldShowRankSettings && (
              <RankSettings
                disabled={isValueEditingDisabled}
                isEnabled={data.style.rankConfig?.isEnabled}
                isRankable={isRankable}
                onCheckRank={rankConfig.onCheck}
                onChangeSlider={rankConfig.onChangeSlider}
                onChangeInput={rankConfig.onChangeInput}
                data={data}
                totalRows={totalRows}
              />
            )}

            {shouldShowStrokeWidthSlider && (
              <StrokeWidthSlider
                value={strokeConfig.value}
                onChange={strokeConfig.onChange}
                disabled={isReadOnly}
              />
            )}
          </AccordionContent>
        </div>
      </AccordionItem>
    );
  }
);

DatasetStyleSelection.displayName = "DatasetStyleSelection";
